import { useEffect, useState } from 'react';
import Spinner from 'react-spinkit';
import SortableTable from '../Tables/SortableTable';
import {
  getCurrentYearTeachersData,
  getLegacyEducatorLogins,
  getNewEducatorLogins,
  getLegacyTimeOnPlatform,
  getNewTimeOnPlatform,
  getTotalRow,
  secondaryUnits,
  lookupNewDBStartDate,
} from '../reportUtilities';
import Tabs from '../Tabs/Tabs';
import Tab from '../Tabs/Tab';
import styles from './TeacherAnalytics.module.scss';
import {
  ReportStudent,
  ReportingMixpanelEvent,
  ReportingPlatformEvent,
  TeacherClassroomMaps,
} from '../../../peekapak-types/ReportDataExchangeTypes';
import { Assignment } from '../../../peekapak-types/LessonPlanTypes';
import { UserProfileType } from '../../../peekapak-types/DataProtocolTypes';
import { EventName } from '../../../peekapak-types/AnalyticsTypes';

interface Props {
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  errorMessage: string;
  assignmentsMap: Record<string, Assignment[]>;
  assignmentsMapReady: boolean;
  assignmentsMapLoadProgress?: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  userList: UserProfileType[];
  userListProgress: string;
}

interface DataMap {
  [key: string]: {
    events: ReportingPlatformEvent[];
    assignments: Assignment[];
  };
}

const TeacherAnalytics = ({
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
  errorMessage,
  assignmentsMap,
  assignmentsMapReady,
  assignmentsMapLoadProgress,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  teacherStudentMap,
  teacherStudentMapReady,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  userList,
}: Props): JSX.Element => {
  const [myState, setMyState] = useState({
    dataMap: {} as DataMap,
    dataMapReady: false,
  });

  useEffect(() => {
    if (!legacyPlatformEventsReady && !legacyPeekavilleEventsReady) {
      setMyState((prevState) => ({
        ...prevState,
        dataMap: {},
        dataMapReady: false,
      }));
    }
  }, [legacyPlatformEventsReady, legacyPeekavilleEventsReady, newMixpanelEventsReady]);

  useEffect(() => {
    if (
      legacyPlatformEventsReady &&
      assignmentsMapReady &&
      legacyPeekavilleEventsReady &&
      teacherStudentMapReady &&
      teacherClassroomMapsReady &&
      !myState.dataMapReady
    ) {
      buildData();
    }

    function buildData() {
      const newMap = {} as DataMap;

      // Get the same list of teachers that will be used for display
      const activeTeachers = getCurrentYearTeachersData(
        userList,
        teacherClassroomMaps,
        legacyPlatformEvents,
        newMixpanelEvents,
      );

      // Build dataMap for each active teacher
      activeTeachers.forEach((teacherObj) => {
        const events = [...legacyPlatformEvents, ...newMixpanelEvents, ...legacyPeekavilleEvents].filter(
          (event) => event.userId === teacherObj.userId || event.distinct_id === teacherObj.email,
        );
        const assignmentsList = teacherStudentMap[teacherObj.email]
          ? teacherStudentMap[teacherObj.email]
              .reduce((acc, student) => {
                return assignmentsMap[student.userId] ? [...acc, ...assignmentsMap[student.userId]] : [...acc];
              }, [] as Assignment[])
              .flat()
          : [];
        newMap[teacherObj.email] = {
          ...teacherObj,
          events,
          assignments: assignmentsList,
        };
      });

      setMyState((prevState) => ({
        ...prevState,
        dataMap: newMap,
        dataMapReady: true,
      }));
    }
  }, [
    legacyPlatformEvents,
    legacyPlatformEventsReady,
    legacyPeekavilleEvents,
    legacyPeekavilleEventsReady,
    newMixpanelEvents,
    newMixpanelEventsReady,
    myState.dataMapReady,
    teacherStudentMap,
    teacherStudentMapReady,
    teacherClassroomMaps,
    teacherClassroomMapsReady,
    userList,
    assignmentsMap,
    assignmentsMapReady,
  ]);

  const message = getDataNotReadyMessage();
  if (message) {
    return message;
  }

  const staticMessage = getStaticMessage();
  if (staticMessage) {
    return staticMessage;
  }

  const activeTeachers = getCurrentYearTeachersData(
    userList,
    teacherClassroomMaps,
    legacyPlatformEvents,
    newMixpanelEvents,
  );

  const sortedList = activeTeachers.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  const tableData = sortedList.map((e) => {
    return {
      full_name: `${e.lastName || ''}, ${e.firstName || ''}`,
      teacher_logins: getNumberOfTeacherLogins(e.email),
      lessons_accessed: getNumberOfElementaryLessonsAccessed(e.email) + getNumberOfSecondaryLessonsAccessed(e.email),
      story_opens: getNumberOfBooksRead(e.email) + getNumberOfSecondaryStoriesOpened(e.email),
      time_on_platform: getTimeOnPlatform(e.email),
      email: e.email,
      userId: e.userId,
    };
  });

  const columns = [
    {
      label: 'Full Name',
      accessor: 'full_name',
      sortable: false,
      renderTooltip: (data: { email?: string; userId?: string }) => data.email || data.userId || null,
    },
    {
      label: 'Teacher Logins',
      accessor: 'teacher_logins',
      sortable: true,
    },
    {
      label: 'Lessons Accessed',
      accessor: 'lessons_accessed',
      sortable: true,
    },
    {
      label: 'Story Opens',
      accessor: 'story_opens',
      sortable: true,
    },
    {
      label: 'Time on Platform (hrs)',
      accessor: 'time_on_platform',
      sortable: true,
    },
  ];

  const totalRow = getTotalRow(tableData);

  const elementaryTableData = sortedList.map((e) => {
    return {
      full_name: `${e.lastName || ''}, ${e.firstName || ''}`,
      book_opens: getNumberOfBooksRead(e.email),
      lessons_accessed: getNumberOfElementaryLessonsAccessed(e.email),
    };
  });

  const elementaryColumns = [
    {
      label: 'Full Name',
      accessor: 'full_name',
      sortable: false,
    },
    {
      label: 'Book Opens',
      accessor: 'book_opens',
      sortable: true,
    },
    {
      label: 'Lessons Accessed',
      accessor: 'lessons_accessed',
      sortable: true,
    },
  ];

  const elementaryTotalRow = getTotalRow(elementaryTableData);

  const secondaryTableData = sortedList.map((e) => {
    return {
      full_name: `${e.lastName || ''}, ${e.firstName || ''}`,
      lessons_accessed: getNumberOfSecondaryLessonsAccessed(e.email),
      assignments_created: getAssignmentsCreated(e.email),
    };
  });

  const secondaryColumns = [
    {
      label: 'Full Name',
      accessor: 'full_name',
      sortable: false,
    },
    {
      label: 'Lessons Accessed',
      accessor: 'lessons_accessed',
      sortable: true,
    },
    {
      label: 'Assignments Created',
      accessor: 'assignments_created',
      sortable: true,
    },
  ];

  const secondaryTotalRow = getTotalRow(secondaryTableData);

  return (
    <Tabs initialTab='Summary' secondary>
      <Tab label='Summary'>
        <SortableTable
          data={tableData}
          columns={columns}
          totals={totalRow}
          defaultSortField='teacher_logins'
          defaultSortOrder='asc'
        />
      </Tab>
      <Tab label='Elementary'>
        <SortableTable data={elementaryTableData} columns={elementaryColumns} totals={elementaryTotalRow} />
      </Tab>
      <Tab label='Secondary'>
        <SortableTable data={secondaryTableData} columns={secondaryColumns} totals={secondaryTotalRow} />
      </Tab>
    </Tabs>
  );

  function getStaticMessage() {
    if (userList.length === 0) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>There are no users in this school</div>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {`We're sorry, there's a problem retrieving data. Please contact
            Peekapak support and provide them with this error message:`}
            {errorMessage}
          </div>
        </div>
      );
    }
  }

  function getDataNotReadyMessage() {
    let message = null;

    if (!teacherStudentMapReady) {
      message = 'Matching students to teachers...';
    } else if (!legacyPeekavilleEventsReady) {
      message = 'Loading game events data...';
    } else if (!assignmentsMapReady) {
      message = `Loading assignments data ${assignmentsMapLoadProgress}...`;
    } else if (!myState.dataMapReady) {
      message = 'Calculating stats...';
    }

    if (message) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {message}
            <Spinner name='line-scale' />
            This may take up to 10 minutes or more
          </div>
        </div>
      );
    }

    return null;
  }

  function getNumberOfTeacherLogins(email: string) {
    const teacher = myState.dataMap[email];
    if (teacher?.events && teacher.events.length > 0) {
      return getLegacyEducatorLogins(teacher.events) + getNewEducatorLogins(teacher.events);
    }
    return 0;
  }

  function getNumberOfBooksRead(email: string) {
    const teacher = myState.dataMap[email];
    const elementaryBookOpenRegexp = new RegExp(/^story.+pg1\s/gm);
    if (teacher?.events && teacher.events.length > 0) {
      const bookOpens: ReportingPlatformEvent[] = teacher.events;
      const numBookOpens = bookOpens.reduce((acc: number, e: ReportingPlatformEvent) => {
        return (e.name.search(elementaryBookOpenRegexp) >= 0 &&
          new Date(e.time) <= lookupNewDBStartDate('completeDate')) ||
          e.name === EventName.LESSONS_BOOK_OPEN ||
          (e.name === EventName.JOURNAL_BOOK_OPEN && e.type === 'Teacher')
          ? acc + 1
          : acc;
      }, 0);
      return numBookOpens;
    }
    return 0;
  }

  function getNumberOfSecondaryStoriesOpened(email: string) {
    const teacher = myState.dataMap[email];
    if (teacher?.events && teacher.events.length > 0) {
      const storyOpens: ReportingPlatformEvent[] = teacher.events;
      const numStoryOpens = storyOpens.reduce((acc: number, e: ReportingPlatformEvent) => {
        return (e.name.includes('featuredstory') && new Date(e.time) <= lookupNewDBStartDate('completeDate')) ||
          e.name === EventName.LESSONS_STORY_OPEN
          ? acc + 1
          : acc;
      }, 0);
      return numStoryOpens;
    }
    return 0;
  }

  function getNumberOfElementaryLessonsAccessed(email: string) {
    const teacher = myState.dataMap[email];
    const elementaryLessonRegexp = /.*lessonPlan.*(bigIdea|readAloud).*Step1\s/;
    if (teacher?.events && teacher.events.length > 0) {
      const lessonsAccessed: ReportingPlatformEvent[] = teacher.events;
      const numLessonsAccessed = lessonsAccessed.reduce((acc: number, e: ReportingPlatformEvent) => {
        return (e.name.search(elementaryLessonRegexp) >= 0 &&
          new Date(e.time) <= lookupNewDBStartDate('completeDate')) ||
          e.name === EventName.LESSONS_OPEN
          ? acc + 1
          : acc;
      }, 0);
      return numLessonsAccessed;
    }
    return 0;
  }

  function getNumberOfSecondaryLessonsAccessed(email: string) {
    const teacher = myState.dataMap[email];
    const secondaryLessonRegexp = /^lessonPlan.+lesson/gm;
    if (teacher?.events && teacher.events.length > 0) {
      const lessonsAccessed: ReportingPlatformEvent[] = teacher.events;
      const numLessonsAccessed = lessonsAccessed.reduce((acc: number, e: ReportingPlatformEvent) => {
        return (e.name.search(secondaryLessonRegexp) >= 0 &&
          new Date(e.time) <= lookupNewDBStartDate('completeDate')) ||
          (e.name === EventName.LESSONS_OPEN && !e.lesson.includes('unit'))
          ? acc + 1
          : acc;
      }, 0);
      return numLessonsAccessed;
    }
    return 0;
  }

  function getTimeOnPlatform(email: string) {
    const teacher = myState.dataMap[email];

    if (teacher?.events && teacher.events.length > 0) {
      return getLegacyTimeOnPlatform(teacher.events) + getNewTimeOnPlatform(teacher.events);
    }
    return 0;
  }

  function getAssignmentsCreated(email: string) {
    const teacher = myState.dataMap[email];
    if (teacher?.assignments && teacher.assignments.length > 0) {
      const concatenateForUniqueness = teacher.assignments.map((a) => `${a.classroomId}${a.todo}`);
      return new Set(concatenateForUniqueness).size;
    }
    return 0;
  }
};

export default TeacherAnalytics;
